import { DynamicForm, FormConfig } from "@components/DynamicForm/DynamicForm";
import { useDocumentDetails } from "@modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { useLocalStorage } from "~/Hooks/useLocalStorage";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { post, put } from "~/Services";
import { centralizedHost } from "~/Utils";
import { decodeHTML } from "~/Utils/CommonUtils";
import { appendSasToken, removeSasFromDescription } from "../../Imagehelper";
import { fields } from "../constants";
import { usePageDocumentDetails } from "./Type.hooks";

export const AddEditCentralizedBannerType = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const { openToast } = useNotification();

  const {
    data: editPageContent,
    error,
    isLoading: drilldownLoading,
    isError,
    refetch: refetchCategories,
  } = usePageDocumentDetails(query.get("editId") || "");

  const {
    data: editPageContentById,
    isLoading: isLoadingLocs,
    refetch: refetchDocument,
  } = useDocumentDetails(query?.get("id") || "");

  const [currentAirport] = useLocalStorage("currentAirport", null);

  useEffect(() => {
    const activeTab = query.get("activeTab");
    if (activeTab === "pageHeading") {
      refetchCategories();
    }
    if (query.get("id")) {
      refetchDocument();
    }
    if (query.get("editId")) {
      refetchCategories();
    }
  }, [query.get("activeTab"), query.get("editId"), query.get("id")]);

  const formConfig: FormConfig = {
    initialValues: {
      image: editPageContentById
        ? editPageContentById.payload?.image
          ? [
              {
                uid: "-1",
                name: "image.jpg",
                status: "done",
                url: appendSasToken(editPageContentById.payload.image),
              },
            ]
          : []
        : editPageContent?.image
          ? [
              {
                uid: "-1",
                name: "image.jpg",
                status: "done",
                url: appendSasToken(editPageContent.image),
              },
            ]
          : [],

      name: editPageContentById
        ? decodeHTML(editPageContentById.payload?.name)
        : editPageContent
          ? decodeHTML(editPageContent?.name)
          : "",

      name_malay: editPageContentById
        ? decodeHTML(editPageContentById.payload?.name_malay)
        : editPageContent
          ? decodeHTML(editPageContent?.name_malay)
          : "",

      name_mandarin: editPageContentById
        ? decodeHTML(editPageContentById.payload?.name_mandarin)
        : editPageContent
          ? decodeHTML(editPageContent?.name_mandarin)
          : "",

      description: editPageContentById
        ? decodeHTML(editPageContentById.payload?.description)
        : editPageContent
          ? decodeHTML(editPageContent?.description)
          : "",

      description_malay: editPageContentById
        ? decodeHTML(editPageContentById.payload?.description_malay)
        : editPageContent
          ? decodeHTML(editPageContent?.description_malay)
          : "",

      description_mandarin: editPageContentById
        ? decodeHTML(editPageContentById.payload?.description_mandarin)
        : editPageContent
          ? decodeHTML(editPageContent?.description_mandarin)
          : "",

      button_text: editPageContentById
        ? decodeHTML(editPageContentById.payload?.button_text)
        : editPageContent
          ? decodeHTML(editPageContent?.button_text)
          : "",

      redirect_url: editPageContentById
        ? decodeHTML(editPageContentById.payload?.redirect_url)
        : editPageContent
          ? decodeHTML(editPageContent?.redirect_url)
          : "",

      project_airport_id: editPageContentById
        ? decodeHTML(editPageContentById.payload.project_airport_id)
        : editPageContent
          ? decodeHTML(editPageContent?.project_airport_id)
          : "",

      published: editPageContentById
        ? decodeHTML(editPageContentById.payload?.published)
        : editPageContent
          ? decodeHTML(editPageContent?.published)
          : "",
    },

    fields: fields.centralizedBannerType,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let imageKeys: any = ["image"];
    for (const key of imageKeys) {
      const field = data[key];

      try {
        if (field?.[0]?.originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "CENTRALIZED");
          const res: any = await post("management/upload-file", formData);
          if (res) data[key] = res.response.data;
        } else if (field?.[0]?.url) {
          const cleanUrl = field[0].url.split("?sp=")[0];
          data[key] = cleanUrl;
        } else if (Array.isArray(field) && field.length === 0) {
          data[key] = "";
        } else if (!field) {
          data[key] = "";
        }
      } catch (error) {
        console.error(`Error handling image field ${key}:`, error);
      }
    }

    let descriptionKeys: any = ["description", "description_malay", "description_mandarin"];
    for (const key of descriptionKeys) {
      if (data[key]) {
        data[key] = removeSasFromDescription(data[key]);
      }
    }

    const payload = {
      action,
      method,
      module: "Centralized Banner",
      subModule: "centralizedBannerType",
      type: "CENTRALIZED",
      application: "CENTRALIZED",
      reviewNotes,
      appList: [{ appName: "CENTRALIZED", apiUrl }],
      payload: {
        ...(action === "UPDATE" && {
          id: editPageContent?.id || editPageContentById?.payload?.id,
        }),
        ...data,
        segregate: "homepagebanner",
      },
      status,
    };

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post(`/management/publish`, payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/centralized/status-information?activeTab=pending");
          } else {
            navigate("/centralized/status-information?activeTab=draft");
          }
        })
        .catch(() => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <ContentWrapper
      title={`${query.get("id") || query.get("editId") ? "Edit" : "Add New"} Type`}
      showBack
    >
      <DynamicForm
        isLoading={drilldownLoading || isLoadingLocs}
        config={formConfig}
        onSubmit={(data, buttonValue, reviewNotes) =>
          onSubmit(
            data,
            `${query.get("editId") || query.get("type") === "update" ? `${centralizedHost}/api/type/${editPageContent?.id || editPageContentById?.payload?.id}` : `${centralizedHost}/api/type`}`,
            `${query.get("editId") || query.get("type") === "update" ? "UPDATE" : "CREATE"}`,
            `${query.get("editId") || query.get("type") === "update" ? "PATCH" : "POST"}`,
            buttonValue == "save" ? "DRAFT" : "PENDING",
            reviewNotes
          )
        }
        defaultReviewNotes="Kindly review this Shop & Dine"
      />
    </ContentWrapper>
  );
};
